import moment from 'moment';

// Billing report as an Excel workbook (sent to the client with the invoice):
//   1. By branch            - billable loans and verified activities per branch
//   2. Loans & activities   - one row per verification
//   3. Billable loans       - one row per loan per month, with activities verified
// Each sheet starts with the client, period and totals. exceljs is loaded
// only when a file is made, so it is not in the page bundle.

const NAVY = 'FF2E3B6E';
const HEADER_FILL = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFE4EAFF' } };
const TOTAL_FILL = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFF2F4FA' } };
const BORDER = { bottom: { style: 'thin', color: { argb: 'FFD7DEEA' } } };

const monthText = (m) => moment(m, 'YYYY-MM').format('MMM YYYY');
const loanText = (l) => (l.loanDeleted ? `Deleted loan (property ${l.propertyId})` : l.loanNumber);
const activitiesOf = (loan) => new Set(loan.verifications.map((v) => v.planId)).size;

// Title block (rows 1-3), a blank row, then the table header on row 5.
const addSheet = (wb, name, info, columns) => {
  const ws = wb.addWorksheet(name, { views: [{ state: 'frozen', ySplit: 5 }] });
  ws.columns = columns.map((c) => ({ key: c.key, width: c.width }));
  ws.getCell('A1').value = `Billing Report – ${info.client}`;
  ws.getCell('A1').font = { bold: true, size: 14, color: { argb: NAVY } };
  ws.getCell('A2').value = `Period: ${info.period}`;
  ws.getCell('A3').value = `Billable loans: ${info.loans.toLocaleString('en-IN')} · Verified activities: ${info.activities.toLocaleString('en-IN')} · Generated ${info.generated}`;
  ws.getCell('A3').font = { color: { argb: 'FF5B6472' } };
  const header = ws.getRow(5);
  header.values = columns.map((c) => c.header);
  header.font = { bold: true, color: { argb: NAVY } };
  header.eachCell((cell) => Object.assign(cell, { fill: HEADER_FILL, border: BORDER }));
  header.height = 20;
  return ws;
};

const addTotal = (ws, values) => {
  const row = ws.addRow(values);
  row.font = { bold: true };
  row.eachCell({ includeEmpty: true }, (cell) => Object.assign(cell, { fill: TOTAL_FILL }));
};

const alignRight = (ws, keys) => keys.forEach((k) => Object.assign(ws.getColumn(k), { alignment: { horizontal: 'right' } }));

export const excelFileName = (client, months) => {
  const span = months.length === 1 ? monthText(months[0])
    : `${monthText(months[0])} to ${monthText(months[months.length - 1])}`;
  return `BrickEZ billing - ${client} - ${span}.xlsx`.replace(/[\\/:*?"<>|]+/g, '-');
};

export default async function downloadExcel({
  report, client, period, months,
}) {
  const { default: ExcelJS } = await import('exceljs');
  const wb = new ExcelJS.Workbook();
  wb.creator = 'BrickEZ';
  wb.created = new Date();

  const multi = months.length > 1;
  const totals = report.months.reduce((t, m) => ({
    loans: t.loans + m.loans, activities: t.activities + m.activities,
  }), { loans: 0, activities: 0 });
  const info = {
    client, period, ...totals, generated: moment().format('D MMM YYYY, h:mm a'),
  };

  // 1. By branch
  const branchSheet = addSheet(wb, 'By branch', info, [
    { key: 'branch', header: 'Branch', width: 34 },
    { key: 'state', header: 'State', width: 18 },
    { key: 'loans', header: multi ? 'Billable loans (sum of months)' : 'Billable loans', width: 18 },
    { key: 'activities', header: 'Verified activities', width: 18 },
  ]);
  report.branches.forEach((b) => branchSheet.addRow({
    branch: b.branchName, state: b.state || '', loans: b.loans, activities: b.activities,
  }));
  addTotal(branchSheet, { branch: 'Total', loans: totals.loans, activities: totals.activities });
  alignRight(branchSheet, ['loans', 'activities']);

  // 2. Loans & activities: one row per verification
  const actSheet = addSheet(wb, 'Loans & activities', info, [
    { key: 'month', header: 'Month', width: 11 },
    { key: 'loan', header: 'Loan number', width: 26 },
    { key: 'customer', header: 'Customer', width: 26 },
    { key: 'branch', header: 'Branch', width: 26 },
    { key: 'state', header: 'State', width: 16 },
    { key: 'activity', header: 'Activity', width: 26 },
    { key: 'floor', header: 'Floor', width: 14 },
    { key: 'verifiedAt', header: 'Verified on', width: 18 },
    { key: 'verifiedBy', header: 'Verified by', width: 22 },
    { key: 'note', header: 'Note', width: 26 },
  ]);
  report.loans.forEach((l) => l.verifications.forEach((v) => {
    const notes = [v.planReset ? 'Plan reset later' : null, l.loanDeleted ? 'Loan deleted' : null];
    actSheet.addRow({
      month: monthText(l.month),
      loan: loanText(l),
      customer: l.custName || '',
      branch: l.branchName,
      state: l.state || '',
      activity: v.activity,
      floor: v.floor || '',
      // Stored as Excel date-time (IST wall clock, no timezone shift).
      verifiedAt: moment.utc(v.verifiedAt, 'YYYY-MM-DD HH:mm:ss').toDate(),
      verifiedBy: v.verifiedBy || '',
      note: notes.filter(Boolean).join(', '),
    });
  }));
  actSheet.getColumn('verifiedAt').numFmt = 'dd-mmm-yyyy hh:mm';

  // 3. Billable loans: one row per loan per month
  const loanSheet = addSheet(wb, 'Billable loans', info, [
    { key: 'month', header: 'Month', width: 11 },
    { key: 'loan', header: 'Loan number', width: 26 },
    { key: 'customer', header: 'Customer', width: 26 },
    { key: 'branch', header: 'Branch', width: 26 },
    { key: 'state', header: 'State', width: 16 },
    { key: 'activities', header: 'Activities verified', width: 18 },
  ]);
  report.loans.forEach((l) => loanSheet.addRow({
    month: monthText(l.month),
    loan: loanText(l),
    customer: l.custName || '',
    branch: l.branchName,
    state: l.state || '',
    activities: activitiesOf(l),
  }));
  addTotal(loanSheet, {
    month: 'Total', loan: `${report.loans.length.toLocaleString('en-IN')} loans`, activities: totals.activities,
  });
  alignRight(loanSheet, ['activities']);

  const buffer = await wb.xlsx.writeBuffer();
  const url = URL.createObjectURL(new Blob([buffer], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  }));
  const a = document.createElement('a');
  a.href = url;
  a.download = excelFileName(client, months);
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

export { activitiesOf };
